import React, { useEffect } from 'react';
import './ThemeWrapper.scss';

import classNames from 'classnames';
import { Container, ContainerProps } from '@components/layout';

export interface ThemeWrapperProps extends ContainerProps {
  children: React.ReactNode;
  theme?: 'light' | 'dark' | 'pluto-dark';
  borderRadiusStyle?: 'round' | 'square' | 'themed';
  target?: 'content' | 'html' | 'body';
  useContainer?: boolean;
}

/**
 * Removes all theme classes from the provided classList, then applies the specified theme.
 * @param theme - The theme to apply ('light' | 'dark' | 'pluto-dark').
 * @param borderRadiusClass - The border radius style to apply ('round' | 'square').
 * @param classList - DOMTokenList to update with the new theme classes.
 */
export const toggleTheme = (
  theme: ThemeWrapperProps['theme'],
  borderRadiusClass: ThemeWrapperProps['borderRadiusStyle'],
  classList: DOMTokenList,
) => {
  removeThemes(classList);
  addTheme(theme, borderRadiusClass, classList);
};

/**
 * Applies theme and border radius CSS classes to the provided classList.
 * @param theme - The theme to apply ('light' | 'dark' | 'pluto-dark').
 * @param borderRadiusClass - The border radius style to apply ('round' | 'square').
 * @param classList - DOMTokenList to add the theme classes to.
 */
export const addTheme = (
  theme: ThemeWrapperProps['theme'],
  borderRadiusClass: ThemeWrapperProps['borderRadiusStyle'],
  classList: DOMTokenList,
) => {
  classList.add(
    'vds-theme-wrapper',
    `vds-theme-wrapper--${borderRadiusClass}`,
    `vds-${theme}-theme`,
    `vds-${theme}-theme__container`,
  );
};

/**
 * Removes all theme and border radius CSS classes from the provided classList.
 * @param classList - DOMTokenList to remove theme classes from.
 */
export const removeThemes = (classList: DOMTokenList) => {
  const themes = ['light', 'dark', 'pluto-dark'];
  const border = ['round', 'square'];
  classList.remove(
    'vds-theme-wrapper',
    ...border.map(border => `vds-theme-wrapper--${border}`),
    ...themes.map(theme => `vds-${theme}-theme`),
    ...themes.map(theme => `vds-${theme}-theme__container`),
  );
};

/**
 * Applies the theme to the specified DOM target ('html' or 'body').
 * @param theme - The theme to apply ('light' | 'dark' | 'pluto-dark').
 * @param borderRadiusClass - The border radius style to apply ('round' | 'square').
 * @param target - The DOM element to apply the theme class to ('html' | 'body').
 */
export const themeHandler = (
  theme: ThemeWrapperProps['theme'],
  borderRadiusClass: ThemeWrapperProps['borderRadiusStyle'],
  target: ThemeWrapperProps['target'],
) => {
  if (target === 'html') {
    const htmlClassList = document.documentElement.classList;
    toggleTheme(theme, borderRadiusClass, htmlClassList);
  } else if (target === 'body') {
    const bodyClassList = document.body.classList;
    toggleTheme(theme, borderRadiusClass, bodyClassList);
  }
};

/**
 * Theme Wrapper Component
 * Use this component at the top level of your application to apply a theme class
 * or to specific content within your application.
 * @param children - Any React.ReactNode component.
 * @param theme - The theme to apply. Default = 'dark'.
 * @param borderRadiusStyle - The border radius style. 'themed' resolves to 'round'. Default = 'round'.
 * @param target - Where to apply the theme class. 'content' scopes the theme to this component's container; 'html'/'body' applies it to the document. Default = 'body'.
 * @param useContainer - Render children inside a Container component (true) or a plain div (false). Default = true.
 * @param className - Additional classes to apply to the container element.
 * @param styles - Inline styles forwarded to the container element.
 */
export const ThemeWrapper: React.FC<ThemeWrapperProps> = ({
  children,
  theme = 'dark',
  borderRadiusStyle = 'round',
  target = 'body',
  className,
  useContainer = true,
  styles,
  ...rest
}) => {
  const ContainerComponent = useContainer ? Container : 'div';
  const styleObj = useContainer ? { styles } : { style: styles };
  const borderRadiusClass = borderRadiusStyle === 'themed' ? 'round' : borderRadiusStyle;

  useEffect(() => {
    themeHandler(theme, borderRadiusClass, target);
  }, [theme, borderRadiusClass, target]);

  if (target === 'html' || target === 'body') {
    return (
      <ContainerComponent className={className} {...styleObj} {...rest}>
        {children}
      </ContainerComponent>
    );
  }

  return (
    <ContainerComponent
      className={classNames(
        'vds-theme-wrapper',
        `vds-theme-wrapper--${borderRadiusClass}`,
        `vds-${theme}-theme`,
        `vds-${theme}-theme__container`,
        className,
      )}
      {...styleObj}
      {...rest}>
      {children}
    </ContainerComponent>
  );
};
